import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Box, Container, Divider, Link, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

import LogoSVG from '../../assets/icons/LogoSVG';

// Reuse the shared legal page styles
import '../termsOfServicePage/styles.scss';

// ── Constants ─────────────────────────────────────────────────────────────────

const LAST_UPDATED  = 'April 11, 2026';
const CONTACT_EMAIL = 'contact@palindev.com';
const SITE_URL      = 'https://notecore.app';

// ── TOC ───────────────────────────────────────────────────────────────────────

const TOC = [
  { id: 's1',  n: '1',  title: 'Introduction'                           },
  { id: 's2',  n: '2',  title: 'Information We Collect'                 },
  { id: 's3',  n: '3',  title: 'How We Collect Your Information'        },
  { id: 's4',  n: '4',  title: 'How We Use Your Information'            },
  { id: 's5',  n: '5',  title: 'Information We Do NOT Collect'          },
  { id: 's6',  n: '6',  title: 'Cookies & Browser Storage'              },
  { id: 's7',  n: '7',  title: 'Third-Party Services'                   },
  { id: 's8',  n: '8',  title: 'Data Sharing & Disclosure'              },
  { id: 's9',  n: '9',  title: 'Data Storage & Security'                },
  { id: 's10', n: '10', title: 'Data Retention'                         },
  { id: 's11', n: '11', title: 'Your Rights & Choices'                  },
  { id: 's12', n: '12', title: "Children's Privacy"                     },
  { id: 's13', n: '13', title: 'International Data Transfers'           },
  { id: 's14', n: '14', title: 'Changes to This Policy'                 },
  { id: 's15', n: '15', title: 'Contact Us'                             },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionHeading = ({ id, n, title }) => (
  <Typography
    id={id}
    variant="h6"
    className="tos-section-heading"
    sx={{ mt: 5, mb: 1.5, fontWeight: 800, fontSize: { xs: 16, sm: 18 }, scrollMarginTop: '90px' }}
  >
    <Box component="span" className="tos-section-number">{n}.</Box>
    {' '}{title}
  </Typography>
);

const P = ({ children, ...props }) => (
  <Typography
    variant="body2"
    className="tos-paragraph"
    sx={{ mb: 1.5, lineHeight: 1.85, fontSize: { xs: 14, sm: 15 }, ...props.sx }}
    {...props}
  >
    {children}
  </Typography>
);

const Sub = ({ n, children }) => (
  <Box className="tos-sub-item">
    <Typography variant="body2" component="span" className="tos-sub-number">{n}</Typography>
    <Typography variant="body2" component="span" className="tos-sub-text" sx={{ lineHeight: 1.85, fontSize: { xs: 14, sm: 15 } }}>
      {children}
    </Typography>
  </Box>
);

const BulletList = ({ items }) => (
  <Box component="ul" className="tos-bullet-list">
    {items.map((item, i) => (
      <Box key={i} component="li" className="tos-bullet-item">
        <Typography variant="body2" sx={{ lineHeight: 1.85, fontSize: { xs: 14, sm: 15 } }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const DataTable = ({ rows }) => (
  <Box className="pp-data-table">
    <Box className="pp-data-table__head">
      <Box className="pp-data-table__cell pp-data-table__cell--head">Data</Box>
      <Box className="pp-data-table__cell pp-data-table__cell--head">Why We Collect It</Box>
      <Box className="pp-data-table__cell pp-data-table__cell--head">How Long We Keep It</Box>
    </Box>
    {rows.map((row, i) => (
      <Box key={i} className="pp-data-table__row">
        <Box className="pp-data-table__cell">
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.82)' }}>
            {row.data}
          </Typography>
        </Box>
        <Box className="pp-data-table__cell">
          <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.6 }}>
            {row.purpose}
          </Typography>
        </Box>
        <Box className="pp-data-table__cell">
          <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.45)' }}>
            {row.retention}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const ThirdPartyCard = ({ name, role, url, policyUrl }) => (
  <Box className="pp-tp-card">
    <Box className="pp-tp-card__name">
      <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: { xs: 13, sm: 14 }, color: 'rgba(255,255,255,0.88)' }}>
        {name}
      </Typography>
      <Box component="span" className="pp-tp-card__role">{role}</Box>
    </Box>
    <Link href={url} target="_blank" rel="noopener noreferrer" className="tos-link" sx={{ fontSize: 12, display: 'block', mt: 0.5 }}>
      {url}
    </Link>
    <Link href={policyUrl} target="_blank" rel="noopener noreferrer" className="tos-link" sx={{ fontSize: 11, opacity: 0.7, display: 'block', mt: 0.25 }}>
      Privacy Policy ↗
    </Link>
  </Box>
);

// ── main ─────────────────────────────────────────────────────────────────────

const PrivacyPolicyPage = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const DATA_TABLE_ROWS = [
    {
      data: 'Google Subject ID (google_sub)',
      purpose: 'Unique identifier from Google used to recognise your account on future sign-ins.',
      retention: 'Until account deletion',
    },
    {
      data: 'Email address',
      purpose: 'Account identification and communication if you contact support.',
      retention: 'Until account deletion',
    },
    {
      data: 'Display name',
      purpose: 'Shown in the profile page and app UI.',
      retention: 'Until account deletion',
    },
    {
      data: 'Profile picture URL',
      purpose: 'Displayed as your avatar in the app. We store the URL only — not the image itself.',
      retention: 'Until account deletion',
    },
    {
      data: 'Note titles & content',
      purpose: 'Core application function — storing and syncing your writing across devices.',
      retention: 'Until you delete the note or your account',
    },
    {
      data: 'Note timestamps',
      purpose: 'Sorting notes by last edited; displaying creation and update dates.',
      retention: 'Until note or account deletion',
    },
    {
      data: 'Subscription plan & status',
      purpose: 'Enforcing plan limits (3 notes vs unlimited) and showing your current plan.',
      retention: 'Until account deletion',
    },
    {
      data: 'Payment order records',
      purpose: 'Legal and financial record-keeping; verifying that a Pro upgrade was paid for.',
      retention: '7 years (tax & legal compliance)',
    },
    {
      data: 'Razorpay order & payment IDs',
      purpose: 'Verifying payment authenticity against Razorpay\'s servers; resolving disputes.',
      retention: '7 years (tax & legal compliance)',
    },    {
      data: 'Anonymous analytics events (pages visited, session duration, button clicks)',
      purpose: 'Understanding how the app is used in aggregate so we can improve the product. Only collected when you give cookie consent.',
      retention: '26 months (Google Analytics default, then auto-deleted)',
    },  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy — NoteCORE</title>
        <meta
          name="description"
          content="NoteCORE Privacy Policy. Learn what data we collect, how we use it, and your rights. We do not sell, rent, or share your personal data. No ads, no tracking."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/privacy`} />

        {/* Open Graph */}
        <meta property="og:type"         content="website" />
        <meta property="og:url"          content={`${SITE_URL}/privacy`} />
        <meta property="og:site_name"    content="NoteCORE" />
        <meta property="og:title"        content="Privacy Policy — NoteCORE" />
        <meta property="og:description"  content="NoteCORE Privacy Policy. We collect only what's necessary to run the app. No ads, no tracking, no data selling." />
        <meta property="og:image"        content={`${SITE_URL}/og.png`} />
        <meta property="og:image:width"  content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content="Privacy Policy — NoteCORE" />
        <meta name="twitter:description" content="NoteCORE Privacy Policy. No ads, no tracking, no data selling." />
        <meta name="twitter:image"       content={`${SITE_URL}/og.png`} />

        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Privacy Policy — NoteCORE',
            description: 'Privacy Policy for NoteCORE writing workspace by Palindev.',
            url: `${SITE_URL}/privacy`,
            dateModified: LAST_UPDATED,
            inLanguage: 'en',
            isPartOf: {
              '@type': 'WebSite',
              name: 'NoteCORE',
              url: SITE_URL,
            },
            publisher: {
              '@type': 'Person',
              name: 'Palindev',
              email: CONTACT_EMAIL,
              url: 'https://palindev.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home',           item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy` },
              ],
            },
          })}
        </script>
      </Helmet>

      <Box className="tos-root">

        {/* ── Header ── */}
        <Box component="header" className="tos-header">
          <Container maxWidth="lg">
            <Box className="tos-header__inner">
              <RouterLink to="/" className="tos-header__logo">
                <LogoSVG height="26px" />
              </RouterLink>
              <Link
                component={RouterLink}
                to="/"
                className="tos-back-link"
                underline="none"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 13 }} />
                Back to Home
              </Link>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" className="tos-container">
          <Box className="tos-layout">

            {/* ── Sidebar TOC ── */}
            <Box component="nav" className="tos-toc" aria-label="Table of contents">
              <Typography variant="overline" className="tos-toc__label">
                Contents
              </Typography>
              <Box component="ol" className="tos-toc__list">
                {TOC.map(({ id, n, title }) => (
                  <Box key={id} component="li" className="tos-toc__item" onClick={() => scrollTo(id)}>
                    <Box component="span" className="tos-toc__num">{n}.</Box>
                    {title}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── Main content ── */}
            <Box component="main" className="tos-content">

              {/* Title */}
              <Box className="tos-hero">
                <Typography variant="h3" fontWeight={900} className="tos-title" sx={{ fontSize: { xs: 28, sm: 36 } }}>
                  Privacy Policy
                </Typography>
                <Typography variant="body2" className="tos-meta">
                  Last updated: <strong>{LAST_UPDATED}</strong>
                </Typography>
                <Box className="tos-summary-box">
                  <Typography variant="body2" sx={{ lineHeight: 1.8, fontSize: { xs: 13, sm: 14 } }}>
                    <strong>The short version:</strong> NoteCORE, operated by <strong>Palindev</strong>,
                    collects only the data that is strictly necessary to run the app — your Google
                    account profile, your notes, and payment records if you upgrade. We use{' '}
                    <strong>Google Analytics</strong> to understand how the app is used (pages visited,
                    session duration, and button clicks). Analytics cookies are only set after you
                    give explicit consent. We run <strong>no advertising</strong> and will{' '}
                    <strong>never sell, rent, or trade your personal data</strong> to anyone. You can
                    request full deletion of your data at any time by emailing{' '}
                    <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>.
                  </Typography>
                </Box>
              </Box>

              <Divider className="tos-divider" sx={{ my: 4 }} />

              {/* Mobile TOC */}
              <Box className="tos-toc-mobile">
                <Typography variant="overline" className="tos-toc__label" sx={{ display: 'block', mb: 1 }}>
                  Jump to section
                </Typography>
                <Box className="tos-toc-mobile__grid">
                  {TOC.map(({ id, n, title }) => (
                    <Box key={id} className="tos-toc-mobile__item" onClick={() => scrollTo(id)}>
                      <Box component="span" className="tos-toc__num tos-toc__num--inline">{n}.</Box> {title}
                    </Box>
                  ))}
                </Box>
                <Divider className="tos-divider" sx={{ mt: 3, mb: 1 }} />
              </Box>

              {/* ─────────────────────────────────────────────────────────────
                  Section 1: Introduction
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s1" n="1" title="Introduction" />
              <P>
                This Privacy Policy ("Policy") describes how <strong>Palindev</strong> ("we", "us", "our"),
                the individual operator of <strong>NoteCORE</strong>, collects, uses, stores, and protects
                your personal information when you use the NoteCORE web application and any related
                services (collectively, the "Service") available at{' '}
                <Link href={SITE_URL} target="_blank" rel="noopener noreferrer" className="tos-link">
                  {SITE_URL}
                </Link>.
              </P>
              <P>
                By using the Service, you agree to the collection and use of information in accordance
                with this Policy. If you do not agree with this Policy, please discontinue your use of
                the Service.
              </P>
              <P>
                This Policy should be read alongside our{' '}
                <Link component={RouterLink} to="/terms" className="tos-link">Terms of Service</Link>,
                which governs your overall use of the Service.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 2: Information We Collect
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s2" n="2" title="Information We Collect" />
              <P>
                We collect the minimum amount of information necessary to provide you with a
                functional, secure note-taking service. Below is a complete inventory of every piece
                of data we store, why we store it, and how long we retain it.
              </P>

              <DataTable rows={DATA_TABLE_ROWS} />

              <P sx={{ mt: 2.5 }}>
                We do not collect any other personal information beyond what is listed in the table
                above. In particular, we do not collect IP addresses, device identifiers, browser
                fingerprints, location data, or behavioral analytics of any kind. See{' '}
                <Box
                  component="span"
                  className="tos-link"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => scrollTo('s5')}
                >
                  Section 5
                </Box>{' '}
                for the full list of what we do not collect.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 3: How We Collect Your Information
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s3" n="3" title="How We Collect Your Information" />
              <Sub n="3.1">
                <strong>Google OAuth 2.0 (at sign-in).</strong> When you click "Continue with
                Google", you are redirected to Google's OAuth consent screen. If you grant permission,
                Google sends us a cryptographically signed ID token containing your Google Subject ID,
                email address, display name, and profile picture URL. We verify this token against
                Google's public keys and store the extracted profile fields in our database. We never
                see your Google password.
              </Sub>
              <Sub n="3.2">
                <strong>Your usage of the editor (when you write notes).</strong> As you type in the
                NoteCORE editor, your note content is autosaved to our servers. We store the title and
                the Editor.js JSON body of each note, along with created and updated timestamps. We do
                not read, analyse, or process your note content for any purpose other than storing and
                returning it to you.
              </Sub>
              <Sub n="3.3">
                <strong>Payment processing (when you purchase Pro).</strong> When you initiate a Pro
                Plan purchase, our server creates a payment order and calls Razorpay's API to generate
                a payment session. Your payment card details are entered directly on Razorpay's
                payment interface and never transmitted to our servers. After payment is confirmed,
                Razorpay sends us a payment ID and order ID, which we store alongside the transaction
                amount, currency, and status for financial record-keeping.
              </Sub>
              <Sub n="3.4">
                <strong>Contact you initiate.</strong> If you send us an email (e.g. for account
                deletion or support), we retain that correspondence to resolve your request. We do not
                add you to any mailing list or contact you for any other purpose.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 4: How We Use Your Information
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s4" n="4" title="How We Use Your Information" />
              <P>We use the information we collect exclusively for the following purposes:</P>
              <BulletList items={[
                'To authenticate you and maintain your account session.',
                'To store, sync, and retrieve your notes across devices.',
                'To display your profile information (name, avatar) in the app UI.',
                'To enforce plan-based feature limits (e.g. the 3-note limit on the Basic plan).',
                'To process and verify your payment for the Pro Plan upgrade.',
                'To maintain financial and legal records required by applicable law.',
                'To respond to support requests or account deletion requests that you initiate.',
                'To detect and prevent fraud, abuse, or security incidents.',
                'To measure anonymous, aggregate usage patterns via Google Analytics — only when you have given explicit cookie consent.',
              ]} />
              <P>
                <strong>We do not use your information for advertising, profiling, automated
                decision-making, or any purpose not listed above.</strong>
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 5: Information We Do NOT Collect
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s5" n="5" title="Information We Do NOT Collect" />
              <P>
                We want to be explicit about what we do not collect, because we believe that is just as
                important as disclosing what we do collect:
              </P>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, my: 2 }}>
                {[
                  { emoji: '📍', label: 'IP addresses' },
                  { emoji: '📱', label: 'Device or hardware identifiers' },
                  { emoji: '🌐', label: 'Browser type or user-agent strings' },
                  { emoji: '🎯', label: 'Advertising or retargeting data' },
                  { emoji: '🗺️', label: 'Location or GPS data' },
                  { emoji: '🖥️', label: 'Screen recordings or heatmaps' },
                  { emoji: '📧', label: 'Email marketing lists' },
                  { emoji: '🔗', label: 'Cross-site tracking identifiers' },
                  { emoji: '🧬', label: 'Biometric data' },
                  { emoji: '🏦', label: 'Full payment card details' },
                ].map(({ emoji, label }) => (
                  <Box key={label} className="pp-not-collected-tag">
                    <Box component="span" sx={{ fontSize: 13 }}>{emoji}</Box>
                    <Typography variant="body2" sx={{ fontSize: { xs: 11, sm: 12 }, color: 'rgba(255,255,255,0.62)', fontWeight: 500 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <P sx={{ mt: 1 }}>
                NoteCORE does not use advertising networks, session-recording tools, or any
                marketing automation. We use Google Analytics <strong>only with your explicit
                consent</strong>, and only in an anonymised, aggregate form — no personal
                data is linked to analytics events.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 6: Cookies & Browser Storage
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s6" n="6" title="Cookies & Browser Storage" />

              <Sub n="6.1">
                <strong>Essential localStorage (no cookies).</strong> The following data is stored
                in your browser's <strong>localStorage</strong> (not cookies). It is
                local to your device and is never automatically transmitted to any server:
              </Sub>
              <BulletList items={[
                'JWT access & refresh tokens — used to authenticate your API requests. Cleared when you sign out.',
                'nc_color_mode — stores your UI colour preference (light or dark mode). No personal data.',
                'Redux application state — stores temporary editor state. Also saved server-side via autosave.',
                'nc_cookie_consent — stores your cookie consent decision (accepted / declined + timestamp). No personal data.',
              ]} />

              <Sub n="6.2">
                <strong>Analytics cookies (opt-in only).</strong> If you click "Accept Analytics"
                on the cookie banner, Google Analytics 4 sets two first-party cookies on your
                browser. These cookies contain <strong>no personally identifiable information</strong>:
              </Sub>

              <Box className="pp-data-table" sx={{ mb: 2 }}>
                <Box className="pp-data-table__head">
                  <Box className="pp-data-table__cell pp-data-table__cell--head">Cookie</Box>
                  <Box className="pp-data-table__cell pp-data-table__cell--head">Expires</Box>
                  <Box className="pp-data-table__cell pp-data-table__cell--head">Purpose</Box>
                </Box>
                {[
                  { data: '_ga',          purpose: 'Assigns an anonymous client ID to distinguish unique visitors.',    retention: '2 years' },
                  { data: '_ga_XXXXXXX',  purpose: 'Maintains session state for the current analytics session.',          retention: '2 years' },
                ].map((row, i) => (
                  <Box key={i} className="pp-data-table__row">
                    <Box className="pp-data-table__cell">
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.82)', fontFamily: 'monospace' }}>{row.data}</Typography>
                    </Box>
                    <Box className="pp-data-table__cell">
                      <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.45)' }}>{row.retention}</Typography>
                    </Box>
                    <Box className="pp-data-table__cell">
                      <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.6 }}>{row.purpose}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Sub n="6.3">
                <strong>Your consent choice.</strong> Analytics cookies are only set after you
                explicitly click "Accept Analytics" on the cookie banner shown on your first
                visit. You can withdraw consent at any time by clearing your browser cookies or
                by emailing{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>{' '}
                to request data deletion from Google Analytics.
              </Sub>
              <Sub n="6.4">
                <strong>IP anonymisation.</strong> We have configured Google Analytics with
                IP anonymisation enabled. Your full IP address is never stored in GA4 — only
                a truncated, anonymised version is used to approximate country-level location.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 7: Third-Party Services
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s7" n="7" title="Third-Party Services" />
              <P>
                NoteCORE relies on the following third-party service providers to operate. Each of
                these providers may process some of your data as part of delivering their service to
                us. We have selected providers we trust and reviewed their privacy practices.
              </P>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, my: 2.5 }}>
                <ThirdPartyCard
                  name="Google Analytics 4"
                  role="Usage analytics (opt-in only)"
                  url="https://analytics.google.com"
                  policyUrl="https://policies.google.com/privacy"
                />
                <ThirdPartyCard
                  name="Google (OAuth 2.0)"
                  role="Authentication provider"
                  url="https://google.com"
                  policyUrl="https://policies.google.com/privacy"
                />
                <ThirdPartyCard
                  name="Razorpay"
                  role="Payment processing"
                  url="https://razorpay.com"
                  policyUrl="https://razorpay.com/privacy"
                />
                <ThirdPartyCard
                  name="Railway"
                  role="Backend API hosting & infrastructure"
                  url="https://railway.app"
                  policyUrl="https://railway.app/legal/privacy"
                />
                <ThirdPartyCard
                  name="Neon"
                  role="Managed PostgreSQL database"
                  url="https://neon.tech"
                  policyUrl="https://neon.tech/privacy-policy"
                />
                <ThirdPartyCard
                  name="Netlify"
                  role="Frontend hosting & CDN"
                  url="https://netlify.com"
                  policyUrl="https://www.netlify.com/privacy"
                />
              </Box>
              <P>
                We do not integrate any advertising networks, session-recording tools (e.g.
                Hotjar, FullStory), or marketing automation services. Google Analytics is
                used exclusively for anonymous, aggregate usage measurement and is only
                active when you have given explicit cookie consent.
              </P>
              <P>
                These providers act as data processors on our behalf. They are contractually
                restricted from using your data for their own independent purposes. Your note content
                is stored encrypted at rest in the Neon database hosted on Railway's infrastructure.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 8: Data Sharing & Disclosure
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s8" n="8" title="Data Sharing & Disclosure" />
              <Box className="tos-warning-box" 
                sx={{ mb: "20px", borderColor: 'rgba(52,211,153,0.25) !important', backgroundColor: 'rgba(52,211,153,0.06) !important' }}>
                <Typography variant="body2" fontWeight={700} sx={{ fontSize: { xs: 13, sm: 14 }, color: 'rgba(52,211,153,0.9) !important' }}>
                  We do not sell, rent, or trade your personal data. Ever.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: 13, sm: 14 }, lineHeight: 1.8, color: 'rgba(255,255,255,0.65) !important' }}>
                  Your personal information is never disclosed to third parties for commercial, marketing,
                  or advertising purposes. The only circumstances in which we share your data are
                  described below.
                </Typography>
              </Box>
              <Sub n="8.1" >
                <strong>Service providers.</strong> We share the minimum necessary data with our
                infrastructure providers (Railway, Neon, Netlify, Razorpay, Google) solely to operate
                the Service, as described in Section 7.
              </Sub>
              <Sub n="8.2">
                <strong>Legal obligation.</strong> We may disclose your information if we are required
                to do so by applicable law, a court order, or a lawful request from a government or
                regulatory authority. We will make reasonable efforts to notify you of such requests
                unless prohibited by law.
              </Sub>
              <Sub n="8.3">
                <strong>Protection of rights.</strong> We may disclose your information if we
                reasonably believe it is necessary to: (a) prevent fraud or abuse of the Service;
                (b) protect the rights, property, or safety of Palindev, our users, or the public;
                or (c) investigate a potential violation of these Terms.
              </Sub>
              <Sub n="8.4">
                <strong>Business transfer.</strong> If Palindev or the NoteCORE Service is acquired,
                transferred, or merged, your information may be transferred as part of that
                transaction. We will notify you before your personal information is transferred and
                becomes subject to a different privacy policy.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 9: Data Storage & Security
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s9" n="9" title="Data Storage & Security" />
              <Sub n="9.1">
                <strong>Where data is stored.</strong> Your data is stored in a managed PostgreSQL
                database provided by Neon, hosted on cloud infrastructure. The backend API that
                processes your requests is deployed on Railway. Both providers use enterprise-grade
                data centres with physical and logical security controls.
              </Sub>
              <Sub n="9.2">
                <strong>Encryption in transit.</strong> All communication between your browser and
                the NoteCORE API is encrypted using TLS (HTTPS). Your data is never transmitted
                over an unencrypted connection.
              </Sub>
              <Sub n="9.3">
                <strong>Encryption at rest.</strong> Data stored in the Neon PostgreSQL database
                is encrypted at rest by the database provider using industry-standard AES-256
                encryption.
              </Sub>
              <Sub n="9.4">
                <strong>Access controls.</strong> Access to the production database is restricted
                to the NoteCORE backend server. No human has routine access to your note content.
                Administrative access is protected by strong credentials and is used only when
                strictly necessary (e.g. to fulfil a deletion request).
              </Sub>
              <Sub n="9.5">
                <strong>Security limitations.</strong> Despite our efforts, no method of transmission
                over the internet or electronic storage is 100% secure. We cannot guarantee absolute
                security. If you become aware of a security vulnerability, please report it to{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 10: Data Retention
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s10" n="10" title="Data Retention" />
              <Sub n="10.1">
                <strong>Active accounts.</strong> We retain your account information and note content
                for as long as your account is active. There is no time limit on active accounts;
                your data persists until you request deletion.
              </Sub>
              <Sub n="10.2">
                <strong>Payment records.</strong> Payment transaction records (order IDs, amounts,
                payment provider IDs) are retained for up to 7 years from the date of the transaction
                to comply with financial record-keeping requirements under applicable Indian tax law
                (GST/Income Tax Act). This retention period applies even after account deletion.
              </Sub>
              <Sub n="10.3">
                <strong>Account deletion.</strong> When you request deletion of your account, we
                permanently delete your account record, Google profile data, and all your notes from
                our active database. Deletion is immediate and irreversible — we do not keep backups
                of your personal data after deletion. Only anonymised payment transaction records
                (as described in 10.2) are retained for the legally required period.
              </Sub>
              <Sub n="10.4">
                <strong>Inactive accounts.</strong> We do not currently have an automatic inactive
                account deletion policy. We may introduce one in the future with advance notice.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 11: Your Rights & Choices
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s11" n="11" title="Your Rights & Choices" />
              <P>
                Depending on your jurisdiction, you may have certain rights regarding your personal
                data. We respect these rights regardless of where you are located.
              </P>
              <Sub n="11.1">
                <strong>Right to access.</strong> You can request a copy of all personal data we hold
                about you at any time by emailing{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>.
                We will respond within a reasonable timeframe.
              </Sub>
              <Sub n="11.2">
                <strong>Right to deletion ("right to be forgotten").</strong> You may request
                complete deletion of your account and all associated personal data by emailing us.
                We will process the deletion promptly. Note that payment transaction records are
                retained for 7 years as described in Section 10.2.
              </Sub>
              <Sub n="11.3">
                <strong>Right to correction.</strong> If any information we hold about you is
                inaccurate (e.g. your name or email changed via Google), you can sign out and sign
                back in to refresh your profile data from Google. For other corrections, contact us
                directly.
              </Sub>
              <Sub n="11.4">
                <strong>Right to portability.</strong> You may request an export of your note data
                in a machine-readable format. Contact us to arrange this.
              </Sub>
              <Sub n="11.5">
                <strong>Right to withdraw analytics consent.</strong> If you previously accepted
                analytics cookies, you can withdraw that consent at any time by clearing your
                browser cookies (which removes the <code>_ga</code> and <code>_ga_XXXXXXX</code>{' '}
                cookies) or by reloading the page after clearing{' '}
                <code>nc_cookie_consent</code> from localStorage. The cookie banner will reappear
                and you can select "Decline analytics".
              </Sub>
              <Sub n="11.6">
                <strong>Right to withdraw service consent.</strong> You may stop using the Service at any
                time. Ceasing use does not automatically delete your account or data; you must
                explicitly request deletion if you want your data removed.
              </Sub>
              <Sub n="11.7">
                <strong>How to exercise your rights.</strong> Email us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>{' '}
                with the subject line "Privacy Request — [Your Request Type]". We will verify your
                identity and respond within 30 days.
              </Sub>

              {/* ─────────────────────────────────────────────────────────────
                  Section 12: Children's Privacy
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s12" n="12" title="Children's Privacy" />
              <P>
                The Service is not directed at individuals under the age of 13. We do not knowingly
                collect personal information from children under 13. If you are a parent or guardian
                and believe that your child has provided us with personal information without your
                consent, please contact us at{' '}
                <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>.
              </P>
              <P>
                Upon receiving such a report, we will promptly verify the claim and, if confirmed,
                permanently delete all data associated with that child's account without notice.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 13: International Data Transfers
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s13" n="13" title="International Data Transfers" />
              <P>
                NoteCORE is built and operated from India. However, the third-party infrastructure
                providers we use (Railway, Neon, Netlify) may store and process data in data centres
                located in the United States and/or the European Union/European Economic Area,
                depending on their infrastructure configuration.
              </P>
              <P>
                If you are accessing the Service from the European Union or United Kingdom, please
                be aware that your data may be transferred to and processed in countries that may not
                have data protection laws equivalent to those in your jurisdiction.
              </P>
              <P>
                By using the Service, you consent to this transfer. We take steps to ensure that our
                service providers implement appropriate security measures (including encryption at rest
                and in transit) to protect your data during such transfers.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 14: Changes to This Policy
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s14" n="14" title="Changes to This Policy" />
              <P>
                We may update this Privacy Policy from time to time to reflect changes in our
                practices, the Service, or applicable law. Any changes will be posted on this page
                with an updated "Last Updated" date. The most current version of this Policy is
                always available at{' '}
                <Link href={`${SITE_URL}/privacy`} className="tos-link" component={RouterLink} to="/privacy">
                  {SITE_URL}/privacy
                </Link>.
              </P>
              <P>
                For material changes that significantly affect how we handle your personal data, we
                will make reasonable efforts to provide notice (such as a prominent banner in the
                app or a notification). Your continued use of the Service after any such changes
                constitutes your acceptance of the updated Policy.
              </P>

              {/* ─────────────────────────────────────────────────────────────
                  Section 15: Contact Us
              ───────────────────────────────────────────────────────────── */}
              <SectionHeading id="s15" n="15" title="Contact Us" />
              <P>
                If you have any questions, concerns, or requests about this Privacy Policy or how
                we handle your personal data, please reach out — we genuinely want to help.
              </P>
              <Box className="tos-contact-box">
                <Typography variant="body2" fontWeight={800} sx={{ mb: 1.5, fontSize: 15 }}>
                  Palindev / NoteCORE
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Email</Box>
                  <Link href={`mailto:${CONTACT_EMAIL}`} className="tos-link">{CONTACT_EMAIL}</Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Website</Box>
                  <Link href="https://palindev.com" target="_blank" rel="noopener noreferrer" className="tos-link">
                    palindev.com
                  </Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">App</Box>
                  <Link href={SITE_URL} target="_blank" rel="noopener noreferrer" className="tos-link">
                    {SITE_URL}
                  </Link>
                </Typography>
                <Typography variant="body2" className="tos-contact-row">
                  <Box component="span" className="tos-contact-label">Jurisdiction</Box>
                  Rajasthan, India
                </Typography>
              </Box>

              <Divider className="tos-divider" sx={{ mt: 5, mb: 3 }} />

              <Box className="tos-footer">
                <Typography variant="caption" className="tos-footer__copy">
                  &copy; {new Date().getFullYear()} Palindev / NoteCORE. All rights reserved.
                </Typography>
                <Box className="tos-footer__links">
                  <Link component={RouterLink} to="/terms" className="tos-footer__link" underline="none">
                    Terms of Service
                  </Link>
                  <Link component={RouterLink} to="/" className="tos-footer__link" underline="none">
                    Home
                  </Link>
                </Box>
              </Box>

            </Box>{/* /tos-content */}
          </Box>{/* /tos-layout */}
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicyPage;
